/* Milano — YouTube click-to-play facade (PDP only) — v1.2, 2026-07-09
 *
 * Problem: a YouTube embed on the Grace PDP (an iframe pasted into the
 * product description — PSI shows /embed/AE7SYmp_byE?si=… plus ~790 KiB of
 * player JS loading during initial page load, ~1.1 s of mobile main-thread).
 *
 * Fix: on product pages, replace any eagerly-loading YouTube iframe found in
 * the initial DOM with a lightweight poster + play button occupying the same
 * box. The real iframe is only created when the shopper taps play.
 *
 * Deliberately skipped:
 *  - Product gallery videos: already deferred by the theme via
 *    [data-deferred-media] poster buttons (snippets/media.liquid).
 *  - Photoswipe zoom content (.pswp).
 *  - Ambient autoplay sections on other pages (color-guide-video,
 *    text-with-video-button): this script only loads on product pages.
 *
 * v1.1 review fixes:
 *  - Rescans can no longer re-facade an already-activated (playing) player.
 *  - Dropped aspect-ratio dependency (old-Safari would collapse to 0 height):
 *    sizing now mirrors the original iframe's width/height attributes, with
 *    aspect-ratio only as progressive enhancement and a padding-bottom
 *    fallback when the embed has no usable dimensions.
 *  - Handles iframes positioned absolutely by a responsive wrapper.
 * v1.2 review fixes:
 *  - Playlist embeds (/embed/videoseries?list=…) are left untouched (no
 *    per-video poster exists for them).
 *  - Poster hides itself if both thumbnail sizes 404 (play button remains).
 *  - Click is contained (preventDefault/stopPropagation) so an iframe pasted
 *    inside a link can't navigate away instead of playing.
 *  - Player iframe receives focus after activation (keyboard flow).
 *  - No reliance on the `inset` shorthand (same old-Safari story as
 *    aspect-ratio).
 *
 * No dependencies. Load with `defer` from layout/theme.liquid inside the
 * existing `request.page_type == 'product'` block.
 */
(function () {
  'use strict';

  var EMBED_RE = /youtube(?:-nocookie)?\.com\/embed\/([A-Za-z0-9_-]{6,})/;
  // Never convert iframes inside these containers:
  //  - theme's deferred gallery media, photoswipe zoom templates
  //  - our own facades (guards rescans against re-converting an active player)
  var SKIP_CLOSEST = '[data-deferred-media], .deferred-media, [data-product-single-media-wrapper], .pswp, .mw-yt-facade';

  var cssInjected = false;
  var warmed = false;

  function injectCss() {
    if (cssInjected) return;
    cssInjected = true;
    var s = document.createElement('style');
    s.textContent =
      '.mw-yt-facade{position:relative;display:block;max-width:100%;background:#000;overflow:hidden}' +
      '.mw-yt-facade__btn{position:absolute;top:0;left:0;width:100%;height:100%;display:block;padding:0;margin:0;border:0;cursor:pointer;background:transparent}' +
      '.mw-yt-facade__poster{position:absolute;top:0;left:0;width:100%;height:100%;object-fit:cover;opacity:.88;transition:opacity .2s ease}' +
      '.mw-yt-facade__btn:hover .mw-yt-facade__poster,.mw-yt-facade__btn:focus-visible .mw-yt-facade__poster{opacity:1}' +
      '.mw-yt-facade__play{position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);width:68px;height:48px;pointer-events:none}' +
      '.mw-yt-facade__play svg{width:100%;height:100%;filter:drop-shadow(0 1px 4px rgba(0,0,0,.4))}' +
      '.mw-yt-facade iframe{position:absolute;top:0;left:0;width:100%;height:100%;border:0}';
    document.head.appendChild(s);
  }

  function playIconSvg() {
    return (
      '<svg viewBox="0 0 68 48" aria-hidden="true" focusable="false">' +
      '<path d="M66.52 7.74a8 8 0 0 0-5.66-5.66C55.79.72 34 .72 34 .72s-21.79 0-26.86 1.36a8 8 0 0 0-5.66 5.66C.12 12.81.12 24 .12 24s0 11.19 1.36 16.26a8 8 0 0 0 5.66 5.66C12.21 47.28 34 47.28 34 47.28s21.79 0 26.86-1.36a8 8 0 0 0 5.66-5.66C67.88 35.19 67.88 24 67.88 24s0-11.19-1.36-16.26Z" fill="#f00"></path>' +
      '<path d="M45 24 27 14v20z" fill="#fff"></path>' +
      '</svg>'
    );
  }

  // Warm the video origins on first hover/touch so playback starts faster.
  // i.ytimg.com is already connected via the poster image.
  function warmConnections() {
    if (warmed) return;
    warmed = true;
    ['https://www.youtube.com', 'https://www.google.com'].forEach(function (origin) {
      var l = document.createElement('link');
      l.rel = 'preconnect';
      l.href = origin;
      document.head.appendChild(l);
    });
  }

  function activate(wrap, src, title) {
    var url;
    try {
      url = new URL(src, window.location.href); // handles protocol-relative //www.youtube.com/…
    } catch (e) {
      return;
    }
    url.searchParams.set('autoplay', '1'); // user just tapped play — start immediately

    var f = document.createElement('iframe');
    f.src = url.href;
    f.title = title || 'Video player';
    f.setAttribute('allow', 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share');
    f.setAttribute('allowfullscreen', '');
    wrap.innerHTML = '';
    wrap.appendChild(f);
    f.focus();
  }

  /* Size the facade to the same box the iframe occupied, so the swap causes
   * no layout shift and behavior matches the original embed:
   *  - width/height attrs (classic 560×315 description embed) → same fixed
   *    box. Where aspect-ratio is supported it shrinks proportionally under
   *    max-width:100%; old browsers keep the original fixed-height behavior.
   *  - width="100%" + px height (snippets/video-iframe.liquid) → same.
   *  - no usable dimensions → fluid 16:9 via padding-bottom (works everywhere,
   *    padding-% is correct because width is 100% of the container).
   */
  function sizeWrap(wrap, iframe) {
    var wAttr = (iframe.getAttribute('width') || '').trim();
    var hAttr = (iframe.getAttribute('height') || '').trim();
    var wNum = /^\d+$/.test(wAttr) ? parseInt(wAttr, 10) : null;
    var hNum = /^\d+(px)?$/.test(hAttr) ? parseInt(hAttr, 10) : null;
    var supportsAR = window.CSS && CSS.supports && CSS.supports('aspect-ratio', '1 / 1');

    if (wNum && hNum) {
      wrap.style.width = wNum + 'px';
      if (supportsAR) {
        wrap.style.aspectRatio = wNum + ' / ' + hNum;
      } else {
        wrap.style.height = hNum + 'px';
      }
    } else if (wAttr.slice(-1) === '%' && hNum) {
      wrap.style.width = wAttr;
      wrap.style.height = hNum + 'px';
    } else {
      wrap.style.width = '100%';
      wrap.style.paddingBottom = '56.25%';
    }

    // If a responsive wrapper positioned the original iframe absolutely
    // (fitvids-style padding box), fill that box instead.
    var cs = window.getComputedStyle ? getComputedStyle(iframe) : null;
    if (cs && cs.position === 'absolute') {
      wrap.style.position = 'absolute';
      wrap.style.top = '0';
      wrap.style.left = '0';
      wrap.style.width = '100%';
      wrap.style.height = '100%';
      wrap.style.paddingBottom = '0';
    }

    // Author inline styles on the iframe (if any) win over our defaults.
    if (iframe.style && iframe.style.cssText) {
      wrap.style.cssText += ';' + iframe.style.cssText;
    }
  }

  function buildFacade(iframe) {
    if (iframe.closest(SKIP_CLOSEST)) return;
    var src = iframe.getAttribute('src') || iframe.getAttribute('data-src') || '';
    var m = src.match(EMBED_RE);
    if (!m) return;
    var videoId = m[1];
    if (videoId === 'videoseries') return; // playlist embed: no per-video poster — leave as-is
    var title = iframe.getAttribute('title') || '';

    injectCss();

    var wrap = document.createElement('div');
    wrap.className = 'mw-yt-facade';
    sizeWrap(wrap, iframe);

    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'mw-yt-facade__btn';
    btn.setAttribute('aria-label', title ? 'Play video: ' + title : 'Play video');

    var img = document.createElement('img');
    img.className = 'mw-yt-facade__poster';
    img.alt = '';
    img.loading = 'lazy';
    img.decoding = 'async';
    img.src = 'https://i.ytimg.com/vi/' + videoId + '/sddefault.jpg';
    img.onerror = function () {
      img.onerror = function () {
        img.onerror = null;
        img.style.display = 'none'; // both sizes 404 — keep black box + play button
      };
      img.src = 'https://i.ytimg.com/vi/' + videoId + '/hqdefault.jpg';
    };

    var play = document.createElement('span');
    play.className = 'mw-yt-facade__play';
    play.innerHTML = playIconSvg();

    btn.appendChild(img);
    btn.appendChild(play);
    wrap.appendChild(btn);

    btn.addEventListener('click', function (e) {
      e.preventDefault();
      e.stopPropagation(); // e.g. embed pasted inside a link must play, not navigate
      activate(wrap, src, title);
    });
    wrap.addEventListener('pointerover', warmConnections, { once: true });
    wrap.addEventListener('touchstart', warmConnections, { once: true, passive: true });

    iframe.parentNode.replaceChild(wrap, iframe);
  }

  function scan(root) {
    var frames = (root || document).querySelectorAll(
      'iframe[src*="youtube.com/embed/"], iframe[src*="youtube-nocookie.com/embed/"], ' +
      'iframe[data-src*="youtube.com/embed/"], iframe[data-src*="youtube-nocookie.com/embed/"]'
    );
    for (var i = 0; i < frames.length; i++) buildFacade(frames[i]);
  }

  // defer ⇒ DOM is parsed when this runs
  scan(document);

  // Re-scan when the theme swaps product-info HTML (combined-listing swaps)
  // and when sections re-render in the theme editor. Activated players are
  // protected by the .mw-yt-facade entry in SKIP_CLOSEST.
  document.addEventListener('theme:product-info:loaded', function (e) {
    scan(e.target && e.target.nodeType === 1 ? e.target : document);
  });
  document.addEventListener('shopify:section:load', function (e) {
    scan(e.target || document);
  });
})();
